/* src/components/testing/countingSpan/CountingSpanSummaryPage.js */
import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Import necessary hooks and context data
import { useProfile } from '../../../contexts/ProfileContext';
import { useSettings } from '../../../contexts/SettingsContext';
import { CST_TEST_TYPES } from '../../../constants/cstConstants';
// Import Firebase services and helpers directly
import { functions, httpsCallable, auth } from '../../../firebaseConfig'; // Use auth directly
import LoadingSpinner from '../../ui/LoadingSpinner';
import PaginationControls from '../../ui/PaginationControls';
import ProgressChart from '../../ui/ProgressChart';
import ConfirmationModal from '../../ui/ConfirmationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faPlay,
  faInfoCircle,
  faBookOpen,
  faExclamationTriangle,
  faHistory,
  faTrophy,
  faChartLine,
  faCheckCircle,
  faClipboardList,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';

// Constants for display
const ITEMS_PER_PAGE = 10;
const CONFIRMATION_WORD = "START";
const DEFAULT_TIMEZONE = 'UTC';
const DISPLAY_MAX_SCORE = 75; // As defined in backend/constants
const DISPLAY_ACCURACY_THRESHOLD = 85; // As defined in backend/constants
const REQUIRED_FAMILIARIZATION_COUNT = 3; // As defined in backend/constants

const CountingSpanSummaryPage = () => {
    // Destructure necessary values from contexts
    // Added authUser directly for clearer dependency management in useEffect
    const { activeProfile, isLoading: isLoadingProfile, loading: loadingAuth, markTutorialCompleted, activeProfileId, authUser } = useProfile();
    const { settings, isLoading: isLoadingSettings } = useSettings();

    const navigate = useNavigate();
    const location = useLocation();

    const [eligibilityStatus, setEligibilityStatus] = useState({
        isLoading: true, isEligible: false, nextAvailableDateString: null, lastAssessmentDate: null,
        familiarizationComplete: false, familiarizationCount: 0, error: null
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmingFamiliarization, setIsConfirmingFamiliarization] = useState(false);

    const eligibilityCheckTimerRef = useRef(null);
    const isMountedRef = useRef(true);
    const justUpdatedRef = useRef(location.state?.justUpdatedCst || false);

    const userTimezoneForDisplay = useMemo(() => settings?.timezone || DEFAULT_TIMEZONE, [settings?.timezone]);

    // --- Lifecycle Management ---
    useEffect(() => {
        // Set mounted ref to true when component mounts
        isMountedRef.current = true;
        // Return cleanup function to set mounted ref to false and clear timer
        return () => {
            isMountedRef.current = false;
            if (eligibilityCheckTimerRef.current) {
                clearTimeout(eligibilityCheckTimerRef.current);
            }
        };
    }, []); // Empty dependency array ensures this runs only on mount and unmount

    // Effect to reset location state if just returned from task
    useLayoutEffect(() => {
        if (location.state?.justUpdatedCst) {
            // Use replace to avoid adding a new entry to the history stack
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    // --- Effect to Fetch Eligibility Status ---
    useEffect(() => {
        // Define the async function to call the Cloud Function
        const callCheckEligibility = async () => {
            // Double-check authUser state as it's a dependency
            if (!authUser || !activeProfileId) {
                console.log("[Eligibility Check Effect] Skipping: No auth user or active profile ID.");
                 if (isMountedRef.current) setEligibilityStatus({ isLoading: false, isEligible: false, error: "User not logged in or no profile selected.", nextAvailableDateString: null, lastAssessmentDate: null, familiarizationComplete: false, familiarizationCount: 0 });
                 return;
            }

            // Set loading state if not already loading
            if (isMountedRef.current && !eligibilityStatus.isLoading) {
                 setEligibilityStatus(prev => ({ ...prev, isLoading: true, error: null }));
            }

            try {
                // --- CRITICAL CHANGE: REMOVED FORCED TOKEN REFRESH ---
                // We no longer call getIdTokenResult(true).
                // The httpsCallable function automatically attaches the current valid token.
                // If the token is expired, the SDK handles refreshing it in the background.
                // This prevents hitting the `auth/quota-exceeded` error.
                // Example: await authUser.getIdToken(); // Only if needed for debugging, NOT for the call below

                // Ensure component is still mounted before proceeding
                if (!isMountedRef.current) return;

                // Get reference to the Cloud Function
                const checkEligibilityFunc = httpsCallable(functions, 'checkCstEligibility');
                // Call the function
                const result = await checkEligibilityFunc({ testType: CST_TEST_TYPES.COUNTING_SPAN });

                // Ensure component is still mounted after the async call returns
                if (!isMountedRef.current) return;

                // Process the result
                const data = result.data || {};
                setEligibilityStatus({
                    isLoading: false,
                    isEligible: data.isEligible ?? false,
                    nextAvailableDateString: data.nextAvailableDateString || null,
                    lastAssessmentDate: data.lastAssessmentDate ? new Date(data.lastAssessmentDate) : null,
                    familiarizationComplete: data.familiarizationComplete ?? false,
                    familiarizationCount: data.familiarizationCount ?? 0,
                    error: null // Clear any previous error on success
                });

            } catch (error) {
                console.error("[Eligibility Check Effect] Error calling function or processing result:", error);
                // Ensure component is still mounted before updating state on error
                if (isMountedRef.current) {
                    setEligibilityStatus(prev => ({
                        ...prev,
                        isLoading: false,
                        // Extract error message, including Firebase specific codes
                        error: `Failed to check eligibility: ${error.code || error.message || 'Unknown error'}`,
                        // Reset status fields on error
                        isEligible: false,
                        familiarizationComplete: false,
                        familiarizationCount: 0,
                        nextAvailableDateString: null,
                        lastAssessmentDate: null
                    }));
                }
            }
        };

        // --- Determine Prerequisites for Calling the Function ---
        // Overall app loading state (Auth, Profile, Settings)
        const overallAppLoading = loadingAuth || isLoadingProfile || isLoadingSettings;
        // Prerequisites met: Not loading AND user is authenticated AND an active profile is selected
        const prerequisitesMet = !overallAppLoading && authUser && activeProfileId;

        // Clear any existing timer before setting a new one
        if (eligibilityCheckTimerRef.current) {
            clearTimeout(eligibilityCheckTimerRef.current);
        }

        if (prerequisitesMet) {
            // If prerequisites are met, call the eligibility check
            // Apply a small delay if we just returned from the CST task, allowing Firestore updates to potentially settle
            const delay = justUpdatedRef.current ? 250 : 0;
            eligibilityCheckTimerRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    callCheckEligibility();
                    // Reset the flag after the delayed check is initiated
                    if (justUpdatedRef.current) {
                         justUpdatedRef.current = false;
                    }
                }
            }, delay);
        } else if (!loadingAuth && !authUser) {
            // If not loading auth, but user is not logged in
            if (isMountedRef.current) setEligibilityStatus({ isLoading: false, isEligible: false, error: "User not logged in.", nextAvailableDateString: null, lastAssessmentDate: null, familiarizationComplete: false, familiarizationCount: 0 });
        } else if (!loadingAuth && authUser && !activeProfileId) {
            // If logged in, but no active profile selected
             if (isMountedRef.current) setEligibilityStatus({ isLoading: false, isEligible: false, error: "No active profile selected.", nextAvailableDateString: null, lastAssessmentDate: null, familiarizationComplete: false, familiarizationCount: 0 });
        } else if (overallAppLoading) {
            // If the app is still loading initial data, ensure loading indicator is shown
            if (isMountedRef.current && !eligibilityStatus.isLoading) {
                setEligibilityStatus(prev => ({ ...prev, isLoading: true, error: null }));
            }
        } else {
            // Catch-all for other states, potentially set loading to false if stuck
              if (isMountedRef.current && eligibilityStatus.isLoading) {
                  setEligibilityStatus(prev => ({ ...prev, isLoading: false, error: prev.error || "Waiting for data..."}));
              }
        }

        // --- Cleanup Function ---
        // This runs when the component unmounts or *before* the effect runs again due to dependency changes.
        return () => {
            if (eligibilityCheckTimerRef.current) {
                clearTimeout(eligibilityCheckTimerRef.current);
            }
        };
    // --- OPTIMIZED DEPENDENCIES ---
    // - loadingAuth, isLoadingProfile, isLoadingSettings: Wait for core data loading.
    // - authUser: Run check when user logs in/out.
    // - activeProfileId: Run check when the active profile changes.
    // Removed: activeProfile, settings (too broad), navigate (stable), eligibilityStatus.isLoading (causes loops)
    }, [loadingAuth, isLoadingProfile, isLoadingSettings, authUser, activeProfileId]);


    // --- Memoized History Data ---

    // Full history including familiarization (for the table)
    const countingSpanHistory = useMemo(() => {
        // Guard against undefined or non-array cstHistory
        if (!activeProfile?.cstHistory || !Array.isArray(activeProfile.cstHistory)) return [];

        const historyWithDates = activeProfile.cstHistory
           .filter(item => item.testType === CST_TEST_TYPES.COUNTING_SPAN && item.timestamp) // Ensure it's CST and has a timestamp
           .map(item => {
                let timestampDate = null;
                try {
                    // Robust timestamp handling: Firestore Timestamp, ISO string, or epoch number
                    if (item.timestamp?.toDate) { // Firestore Timestamp object
                        timestampDate = item.timestamp.toDate();
                    } else if (typeof item.timestamp === 'string') { // ISO String
                        timestampDate = new Date(item.timestamp);
                    } else if (typeof item.timestamp === 'number') { // Milliseconds epoch
                         timestampDate = new Date(item.timestamp);
                    }
                } catch (e) { console.warn("Error parsing date for history item:", item.timestamp, e); }
                // Return item with parsed Date object, or keep original if parsing failed (will be filtered)
                return { ...item, timestamp: timestampDate };
           })
           // Filter out items where timestamp parsing failed or resulted in an invalid Date
           .filter(item => item.timestamp instanceof Date && !isNaN(item.timestamp));

        // Sort newest first for the history table display
        return historyWithDates.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    }, [activeProfile?.cstHistory]); // Depend only on the specific history field

    // Filtered history excluding familiarization (for the chart)
    const assessmentHistoryForChart = useMemo(() => {
        const filtered = countingSpanHistory.filter(item => !item.familiarizationSession);
        // Chart expects data sorted chronologically (oldest first), so reverse the already sorted array
        return [...filtered].reverse(); // Use spread to create a new array before reversing
    }, [countingSpanHistory]);

    // Get best score for display
    const bestScore = useMemo(() => {
        if (!assessmentHistoryForChart.length) return null;
        // Use reduce to find the item with the highest scorePCU
        return assessmentHistoryForChart.reduce((max, item) => {
            // Ensure scorePCU is a number and greater than current max
            return (typeof item.scorePCU === 'number' && item.scorePCU > max.score)
                ? { score: item.scorePCU, date: item.timestamp }
                : max;
        }, { score: -Infinity, date: null }); // Initialize with lowest possible score
    }, [assessmentHistoryForChart]);

    const totalItems = countingSpanHistory.length; // Table pagination based on total history
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    // Paginated History for Table Display
    const paginatedHistory = useMemo(() => {
       const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
       // Paginate the full history (including familiarization)
       return countingSpanHistory.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [countingSpanHistory, currentPage]);

    // Reset page number if the history data changes (e.g., new session added)
    useEffect(() => { setCurrentPage(1); }, [countingSpanHistory]);

    // --- Actions ---
    const handleReviewTutorial = useCallback(() => {
        navigate('/testing/counting-span/tutorial');
    }, [navigate]);

    const handleStartClick = useCallback(() => {
        if (eligibilityStatus.isLoading) return; // Prevent clicks while loading eligibility

        const startFamiliarization = !eligibilityStatus.familiarizationComplete;
        if (startFamiliarization) {
            // Directly open modal for familiarization without checking eligibility
            setIsConfirmingFamiliarization(true);
            setIsModalOpen(true);
        } else {
            // Only open modal if eligible for monthly assessment
            if (eligibilityStatus.isEligible) {
                setIsConfirmingFamiliarization(false);
                setIsModalOpen(true);
            }
            // Do nothing if familiarization is complete but not eligible
        }
    }, [eligibilityStatus]); // Dependencies: eligibilityStatus

    const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

    const handleConfirmStart = useCallback(async () => {
        setIsModalOpen(false); // Close modal immediately

        const tutorialKey = CST_TEST_TYPES.COUNTING_SPAN;
        // Check if tutorial needs marking (only if profile exists)
        if (activeProfile && activeProfileId && !activeProfile.tutorialsCompleted?.[tutorialKey]) {
            // Mark tutorial complete optimistically in the background
            // No need to await, let it happen without blocking navigation
            markTutorialCompleted(activeProfileId, tutorialKey)
                .catch(err => console.error("Failed to mark tutorial complete in background:", err));
        }
        // Navigate to the task running screen
        navigate('/testing/counting-span/run');
    }, [navigate, activeProfileId, activeProfile, markTutorialCompleted]); // Dependencies needed for the checks/actions


    // --- Helper Function for Date Formatting ---
     const formatDate = (dateInput) => {
        if (!dateInput) return 'N/A';
        try {
            // Ensure we have a Date object
            const date = (dateInput instanceof Date) ? dateInput : new Date(dateInput);
            // Check if the date is valid
            if (isNaN(date.getTime())) return 'Invalid Date';
            // Format using user's locale and specified options
            // Explicitly setting timeZone might be needed if backend stores UTC and you want consistent display
            // across user machines regardless of their OS settings. Let's use the context setting.
            return date.toLocaleString(undefined, { // Use browser's default locale
                year: 'numeric', month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit', hour12: true,
                timeZone: userTimezoneForDisplay // Use timezone from settings context
            });
        } catch (e) { console.error("Error formatting date:", dateInput, e); return 'Invalid Date'; }
    };

    // Get the formatCombinedDifficulty function from context or define it here if needed
    // Assuming it's not in context, define locally:
    const formatCombinedDifficulty = (nBack = 2, difficulty = 0) => { // Add default values
        const validNBack = typeof nBack === 'number' ? nBack : 2;
        const validDifficulty = typeof difficulty === 'number' ? difficulty : 0;
        return `${validNBack}.${(validDifficulty * 100).toFixed(0).padStart(2, '0')}`;
    };

    // --- Loading and Error States ---
     const isAppLoading = loadingAuth || isLoadingProfile || isLoadingSettings;
     if (isAppLoading) {
         return <LoadingSpinner message="Loading profile and settings..." />;
     }
     // Use authUser from context which reflects login state
     if (!authUser && !loadingAuth) {
          return <div className="p-4 text-center text-red-600">Please log in to view assessments.</div>;
     }
     // Check if user is logged in but profile is missing (still loading or not created)
     if (authUser && !activeProfile && !isLoadingProfile) {
         return ( <div className="p-4 text-center text-gray-600"> Please select or create a profile to view assessments. </div> );
     }

    // --- Derived state for rendering ---
    const { isEligible, familiarizationComplete, familiarizationCount, nextAvailableDateString, lastAssessmentDate, error: eligibilityError } = eligibilityStatus;
    const isFamiliarizationNeeded = !familiarizationComplete;
    const familiarizationSessionsRemaining = Math.max(0, REQUIRED_FAMILIARIZATION_COUNT - familiarizationCount);

    let startButtonText, startButtonDisabled, startButtonIcon;
    // Disable button if app is loading OR eligibility check is running
    const disableButtonWhileLoading = isAppLoading || eligibilityStatus.isLoading;

    if (isFamiliarizationNeeded) {
        startButtonText = "Start Familiarization Session";
        // Button is only disabled if data is loading
        startButtonDisabled = disableButtonWhileLoading;
        startButtonIcon = faPlay;
    } else {
        startButtonText = "Start Monthly Assessment";
        // Button disabled if loading OR if familiarization complete but not eligible
        startButtonDisabled = disableButtonWhileLoading || !isEligible;
        startButtonIcon = isEligible ? faPlay : faLock;
    }

    // --- Render JSX ---
    return (
        <div className="w-full container mx-auto px-4 sm:px-6 md:px-6">
            {/* Page Header Area */}
            <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                 {/* Header content remains the same... */}
                 <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="text-gray-800">
                            <h1 className="text-2xl font-bold flex items-center">
                                <FontAwesomeIcon icon={faClipboardList} className="mr-3 text-indigo-600" />
                                Counting Span Assessment
                            </h1>
                            <p className="mt-1 text-gray-600">Measure and track your working memory capacity over time</p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate('/testing')}
                                className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1"
                            >
                                ← Back to Assessments
                            </button>
                        </div>
                    </div>
                </div>

                 {/* Description and Best Score section remains the same... */}
                 <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-grow">
                            <p className="text-gray-700">
                                The Counting Span task measures your working memory - the mental workspace that helps you temporarily store and manipulate information.
                                Complete {REQUIRED_FAMILIARIZATION_COUNT} practice sessions first, then take a formal assessment once each month to track your progress.
                            </p>
                        </div>

                        {bestScore && bestScore.score > -Infinity && ( // Check against initial value
                            <div className="flex-shrink-0 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-center">
                                <div className="flex items-center justify-center text-amber-600 mb-1">
                                    <FontAwesomeIcon icon={faTrophy} className="text-amber-500 mr-2" />
                                    <span className="font-semibold">Personal Best</span>
                                </div>
                                {/* Display score only if it's a valid number */}
                                {typeof bestScore.score === 'number' && (
                                     <div className="text-2xl font-bold text-gray-800">{bestScore.score} <span className="text-sm font-normal text-gray-500">/ {DISPLAY_MAX_SCORE}</span></div>
                                )}
                                <div className="text-xs text-gray-500 mt-1">{formatDate(bestScore.date)}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content Grid - Changed to a single column until a larger breakpoint */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
                {/* Start Button & Status Card */}
                <div className="xl:col-span-1">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col"> {/* Ensure full height */}
                        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50"> {/* Card Header container */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-800 flex items-center flex-shrink">
                                    <FontAwesomeIcon icon={faPlay} className="mr-2 text-indigo-600" />
                                    Assessment Status
                                </h2>
                            </div>
                        </div> {/* <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< FIXED: Added closing tag here */}

                        <div className="p-6 flex-grow flex flex-col justify-between"> {/* Card Body container */}
                             <div> {/* Group for status indicators */}
                                {/* Familiarization Progress Indicator */}
                                {isFamiliarizationNeeded && (
                                    <div className="mb-4">
                                        {/* ... familiarization progress bar ... */}
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm font-medium text-gray-700">Familiarization Progress</span>
                                            <span className="text-sm font-semibold text-indigo-700">{familiarizationCount} / {REQUIRED_FAMILIARIZATION_COUNT}</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div
                                                className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                                                style={{ width: `${(familiarizationCount / REQUIRED_FAMILIARIZATION_COUNT) * 100}%` }}
                                            ></div>
                                        </div>
                                        <p className="mt-3 text-sm text-gray-600">
                                            Complete {familiarizationSessionsRemaining} more familiarization session{familiarizationSessionsRemaining !== 1 ? 's' : ''} to unlock monthly assessments.
                                        </p>
                                    </div>
                                )}

                                {/* Monthly Assessment Status */}
                                {familiarizationComplete && (
                                    <div className={`rounded-lg p-4 mb-4 ${isEligible ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                                        {/* ... eligibility status message ... */}
                                        {isEligible && (
                                            <div className="flex items-center text-green-700">
                                                <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-600" />
                                                <span className="font-medium">Ready for this month's assessment!</span>
                                            </div>
                                        )}
                                        {!isEligible && nextAvailableDateString && (
                                            <div>
                                                <div className="flex items-center text-amber-700 font-medium mb-1">
                                                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-amber-600" />
                                                    <span>Next assessment available:</span>
                                                </div>
                                                <p className="text-gray-700 font-semibold">{nextAvailableDateString}</p>
                                                {lastAssessmentDate && (
                                                    <p className="mt-2 text-xs text-gray-500">
                                                        Last assessment completed: {formatDate(lastAssessmentDate)}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                         {/* Handle case where familiarization is complete but next date isn't known yet (e.g., initial load) */}
                                        {!isEligible && !nextAvailableDateString && !eligibilityStatus.isLoading && (
                                            <div className="flex items-center text-amber-700">
                                                 <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-amber-600" />
                                                 <span className="font-medium">Checking next availability...</span>
                                             </div>
                                        )}
                                    </div>
                                )}

                                {/* Eligibility Error Display */}
                                {eligibilityError && (
                                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm break-words"> {/* Allow long errors to wrap */}
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                        {eligibilityError}
                                    </div>
                                )}
                             </div>

                            <div> {/* Group for button and requirements */}
                                {/* Start/Action Button */}
                                {!disableButtonWhileLoading ? (
                                    <>
                                        <button
                                            onClick={handleStartClick}
                                            disabled={startButtonDisabled}
                                            className={`w-full bg-indigo-600 text-white py-3 px-3 rounded-lg hover:bg-indigo-700 font-medium
                                            text-sm transition duration-150 shadow-sm hover:shadow focus:outline-none
                                            focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center
                                            ${startButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            <FontAwesomeIcon icon={startButtonIcon} className={`mr-2 ${startButtonDisabled ? 'opacity-50' : ''}`} />
                                            <span className="hidden md:hidden lg:hidden xl:inline whitespace-nowrap">{startButtonText}</span>
                                            <span className="md:hidden inline-block whitespace-nowrap">{startButtonText}</span>
                                            <span className="hidden md:inline lg:inline xl:hidden inline-block whitespace-nowrap">Start</span>
                                        </button>

                                        {/* New prominent How To Play button */}
                                        <button
                                            onClick={handleReviewTutorial}
                                            className="w-full mt-3 bg-blue-50 border border-blue-200 text-blue-700 py-2.5 px-3 rounded-lg
                                            hover:bg-blue-100 font-medium text-sm transition duration-150 shadow-sm
                                            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center"
                                        >
                                            <FontAwesomeIcon icon={faBookOpen} className="mr-2" />
                                            <span className="hidden md:hidden lg:hidden xl:inline whitespace-nowrap">View Instructions & Tutorial</span>
                                            <span className="md:hidden inline-block whitespace-nowrap">View Instructions & Tutorial</span>
                                            <span className="hidden md:inline lg:inline xl:hidden inline-block whitespace-nowrap">Tutorial</span>
                                        </button>
                                    </>
                                ) : (
                                    <div className="w-full bg-indigo-600 opacity-70 text-white py-3 px-3 rounded-lg font-medium
                                    text-sm shadow-sm flex items-center justify-center animate-pulse">
                                        <div className="w-5 h-5 mr-3">
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </div>
                                        <span className="hidden md:hidden lg:hidden xl:inline whitespace-nowrap">Checking eligibility...</span>
                                        <span className="md:hidden inline-block whitespace-nowrap">Checking eligibility...</span>
                                        <span className="hidden md:inline lg:inline xl:hidden inline-block whitespace-nowrap">Loading...</span>
                                    </div>
                                )}

                                {/* Additional Requirements Info */}
                                <div className="mt-4 text-sm text-gray-600">
                                     {/* ... requirements list ... */}
                                     <h3 className="font-medium text-gray-700 mb-2">Requirements:</h3>
                                    <ul className="space-y-2">
                                        <li className="flex items-start">
                                            <span className={`mt-0.5 mr-2 flex-shrink-0 h-4 w-4 rounded-full ${familiarizationComplete ? 'bg-green-500' : 'bg-gray-300'} transition-colors`}></span>
                                            <span>Complete {REQUIRED_FAMILIARIZATION_COUNT} familiarization sessions</span>
                                        </li>
                                        <li className="flex items-start">
                                            {/* Show green check only if eligible, otherwise gray */}
                                            <span className={`mt-0.5 mr-2 flex-shrink-0 h-4 w-4 rounded-full ${familiarizationComplete && isEligible ? 'bg-green-500' : 'bg-gray-300'} transition-colors`}></span>
                                            <span>Wait {!isEligible && nextAvailableDateString ? `until ${nextAvailableDateString}` : 'one full month'} between assessments</span>
                                        </li>
                                        <li className="flex items-start">
                                            {/* This doesn't have a direct state, so it's always gray indicating a general goal */}
                                            <span className="mt-0.5 mr-2 flex-shrink-0 h-4 w-4 rounded-full bg-gray-300"></span>
                                            <span>Aim for processing accuracy above {DISPLAY_ACCURACY_THRESHOLD}%</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div> {/* End of Card Body container */}
                    </div> {/* End of bg-white card container */}
                </div> {/* End of xl:col-span-1 */}

                {/* Chart & History Area */}
                <div className="xl:col-span-2">
                    {/* Performance Trend Chart */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
                         {/* ... chart header ... */}
                         <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                                <FontAwesomeIcon icon={faChartLine} className="mr-2 text-indigo-600" />
                                Performance Trend
                                <span className="ml-2 inline-flex items-center text-xs font-normal text-gray-500 group relative cursor-help">
                                    <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                                    <span>PCU Score</span>
                                    <span className="absolute top-full mt-2 w-56 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none left-0 z-10">
                                        PCU (Partial-Credit Unit) measures your working memory capacity, awarding points for correctly recalled items in their proper positions.
                                    </span>
                                </span>
                            </h2>
                        </div>

                        <div className="p-6 min-h-[300px] flex items-center justify-center"> {/* Ensure min height for chart area */}
                            {assessmentHistoryForChart.length > 0 ? (
                                <ProgressChart
                                    historyData={assessmentHistoryForChart}
                                    testType={CST_TEST_TYPES.COUNTING_SPAN}
                                    maxPossibleScore={DISPLAY_MAX_SCORE}
                                />
                            ) : (
                                 <div className="text-center py-16 px-4">
                                     {/* ... no data message ... */}
                                    <div className="bg-gray-100 inline-flex items-center justify-center w-16 h-16 rounded-full mb-4">
                                        <FontAwesomeIcon icon={faChartLine} className="text-gray-400 text-2xl" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-700 mb-2">No Assessment Data Yet</h3>
                                    <p className="text-gray-500 max-w-md mx-auto">
                                        Complete your familiarization sessions and then take your first monthly assessment to start tracking your progress.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div> {/* End of xl:col-span-2 */}
            </div> {/* End of main grid */}

            {/* Session History - Redesigned for better mobile experience */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
                {/* Table Header */}
                <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                        <FontAwesomeIcon icon={faHistory} className="mr-2 text-indigo-600" />
                        Session History
                    </h2>
                </div>

                <div className="p-4">
                    {paginatedHistory.length > 0 ? (
                        <>
                            {/* Mobile Card View (visible on small screens) */}
                            <div className="block md:hidden">
                                {paginatedHistory.map((item, index) => (
                                    <div 
                                        key={item.id || item.timestamp?.toString() || index}
                                        className="mb-4 p-3 border rounded-lg border-gray-200 hover:bg-gray-50 transition-colors duration-100"
                                    >
                                        <div className="flex justify-between items-center mb-2">
                                            <div>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    item.familiarizationSession
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-indigo-100 text-indigo-800'
                                                }`}>
                                                    {item.familiarizationSession ? 'Practice' : 'Assessment'}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {formatDate(item.timestamp)}
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                            <div>
                                                <div className="text-xs text-gray-500">Score (PCU)</div>
                                                <div className="font-medium">
                                                    {typeof item.scorePCU === 'number' ? (
                                                        <div className="flex items-center">
                                                            <span className="text-gray-900">{item.scorePCU}</span>
                                                            <span className="text-gray-400 ml-1">/ {DISPLAY_MAX_SCORE}</span>
                                                        </div>
                                                    ) : 'N/A'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500">Processing Acc.</div>
                                                {typeof item.processingAccuracy === 'number' ? (
                                                    <div className={`px-2 py-0.5 inline-block rounded-full text-xs font-medium ${
                                                        item.flaggedLowAccuracy
                                                            ? 'bg-orange-100 text-orange-800'
                                                            : 'bg-green-100 text-green-800'
                                                    }`}>
                                                        {item.processingAccuracy.toFixed(1)}%
                                                        {item.flaggedLowAccuracy && (
                                                            <FontAwesomeIcon
                                                                icon={faExclamationTriangle}
                                                                className="ml-1 text-amber-500"
                                                                title={`Accuracy below ${DISPLAY_ACCURACY_THRESHOLD}% threshold`}
                                                            />
                                                        )}
                                                    </div>
                                                ) : 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Traditional Table (hidden on small screens, visible on medium and up) */}
                            <div className="hidden md:block overflow-auto">
                                <table className="w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score (PCU)</th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processing Acc.</th>
                                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {paginatedHistory.map((item, index) => (
                                            <tr key={item.id || item.timestamp?.toString() || index} className="hover:bg-gray-50 transition-colors duration-100">
                                                <td className="px-4 py-4 text-sm text-gray-700">
                                                    {formatDate(item.timestamp)}
                                                </td>
                                                <td className="px-4 py-4 text-sm font-medium">
                                                    {typeof item.scorePCU === 'number' ? (
                                                        <div className="flex items-center">
                                                            <span className="text-gray-900">{item.scorePCU}</span>
                                                            <span className="text-gray-400 ml-1">/ {DISPLAY_MAX_SCORE}</span>
                                                        </div>
                                                    ) : 'N/A'}
                                                </td>
                                                <td className="px-4 py-4">
                                                    {typeof item.processingAccuracy === 'number' ? (
                                                        <div className="flex items-center">
                                                            <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                item.flaggedLowAccuracy
                                                                    ? 'bg-orange-100 text-orange-800'
                                                                    : 'bg-green-100 text-green-800'
                                                            }`}>
                                                                {item.processingAccuracy.toFixed(1)}%
                                                                {item.flaggedLowAccuracy && (
                                                                    <FontAwesomeIcon
                                                                        icon={faExclamationTriangle}
                                                                        className="ml-1 text-amber-500"
                                                                        title={`Accuracy below ${DISPLAY_ACCURACY_THRESHOLD}% threshold`}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : 'N/A'}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-600">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        item.familiarizationSession
                                                            ? 'bg-blue-100 text-blue-800'
                                                            : 'bg-indigo-100 text-indigo-800'
                                                    }`}>
                                                        {item.familiarizationSession ? 'Practice' : 'Assessment'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Pagination controls */}
                            {totalPages > 1 && (
                                <div className="mt-4">
                                    <PaginationControls
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={setCurrentPage}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        totalItems={totalItems}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-16 px-4">
                            {/* No History Message */}
                            <div className="bg-gray-100 inline-flex items-center justify-center w-16 h-16 rounded-full mb-4">
                                <FontAwesomeIcon icon={faHistory} className="text-gray-400 text-2xl" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-700 mb-2">No History Available</h3>
                            <p className="text-gray-500 max-w-md mx-auto">
                                Start with a familiarization session to begin building your performance history.
                            </p>
                            {isFamiliarizationNeeded && !disableButtonWhileLoading && (
                                <button
                                    onClick={handleStartClick}
                                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    <FontAwesomeIcon icon={faArrowRight} className="mr-2" />
                                    Start Now
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
                {/* ... FAQ Header ... */}
                <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-indigo-600" />
                        Frequently Asked Questions
                    </h2>
                </div>
                 {/* ... FAQ Content (details/summary elements) ... */}
                 <div className="p-6">
                    <div className="space-y-6">
                        {/* Kept FAQ content the same as previous version */}
                         {/* FAQ Item 1 */}
                        <div className="border-b border-gray-200 pb-4">
                            <details className="group">
                                <summary className="flex justify-between items-center cursor-pointer list-none">
                                    <span className="text-gray-800 font-medium">What is the Counting Span task?</span>
                                    <span className="transition group-open:rotate-180">
                                        <svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                                    </span>
                                </summary>
                                <div className="mt-3 text-gray-600 text-sm"><p>The Counting Span task measures your working memory capacity. During the task, you will see screens with different shapes. Your job is to count the number of specific target shapes (Blue Circles) on each screen, decide if that count is Odd or Even, AND remember the sequence of counts you made. After several screens, you'll be asked to recall the counts in the exact order they appeared.</p></div>
                            </details>
                        </div>
                         {/* FAQ Item 2 */}
                         <div className="border-b border-gray-200 pb-4">
                            <details className="group">
                                <summary className="flex justify-between items-center cursor-pointer list-none">
                                    <span className="text-gray-800 font-medium">What does this task measure?</span>
                                     <span className="transition group-open:rotate-180"><svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg></span>
                                </summary>
                                <div className="mt-3 text-gray-600 text-sm"><p>It assesses Working Memory Capacity (WMC) – your ability to simultaneously store temporary information (the counts) while actively processing other information (counting the shapes and making odd/even judgments). This is different from simply memorizing a list, as it involves managing attention between two demanding tasks.</p></div>
                            </details>
                        </div>
                         {/* ... other FAQ items ... */}
                        <div className="border-b border-gray-200 pb-4"><details className="group"><summary className="flex justify-between items-center cursor-pointer list-none"><span className="text-gray-800 font-medium">What are Familiarization sessions for? Do they count?</span><span className="transition group-open:rotate-180"><svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg></span></summary><div className="mt-3 text-gray-600 text-sm"><p>The first three sessions are for familiarization. Cognitive tasks often show rapid improvement initially as you learn the rules and get comfortable with the interface. These sessions help you get past that steep learning curve so that later assessments better reflect your stable working memory capacity. Familiarization scores are recorded in your history table but do not count towards your monthly assessment limit and are excluded from the main performance trend chart to give a clearer view of your assessment progress.</p></div></details></div>
                        <div className="border-b border-gray-200 pb-4"><details className="group"><summary className="flex justify-between items-center cursor-pointer list-none"><span className="text-gray-800 font-medium">How often can I take the main assessment?</span><span className="transition group-open:rotate-180"><svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg></span></summary><div className="mt-3 text-gray-600 text-sm"><p>After completing the 3 familiarization sessions, you can take one official assessment per calendar month. This limit ensures the assessment remains a stable measure and minimizes the influence of excessive practice on the score itself. Your eligibility status and the date the next assessment becomes available are shown above.</p></div></details></div>
                        <div className="border-b border-gray-200 pb-4"><details className="group"><summary className="flex justify-between items-center cursor-pointer list-none"><span className="text-gray-800 font-medium">How is the "PCU Score" calculated?</span><span className="transition group-open:rotate-180"><svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg></span></summary><div className="mt-3 text-gray-600 text-sm"><p>PCU stands for Partial Credit Unit scoring. You get one point for every count you recall correctly and in its original serial position within a set. For example, if the correct sequence was [5, 8, 4] and you recalled [5, ?, 4], you would get 2 points for that set (one for the 5 in position 1, one for the 4 in position 3). The maximum possible score is 75. This method is better at detecting small improvements than requiring perfect recall of every set.</p></div></details></div>
                        <div className="border-b border-gray-200 pb-4"><details className="group"><summary className="flex justify-between items-center cursor-pointer list-none"><span className="text-gray-800 font-medium">What is "Processing Accuracy" and why the 85% threshold?</span><span className="transition group-open:rotate-180"><svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg></span></summary><div className="mt-3 text-gray-600 text-sm"><p>Processing Accuracy refers to how accurately you made the "Odd" or "Even" judgments after counting the blue circles. It's crucial to perform well on both the counting/judging part and the remembering part. The 85% threshold is a standard check used in research. If your accuracy is below 85%, it might suggest you were focusing too much on remembering the counts and not enough on accurately performing the counting/judging task. Your score is still recorded, but it should be interpreted with caution as it might not fully reflect your WMC under true dual-task conditions.</p></div></details></div>
                        <div className="border-b border-gray-200 pb-4"><details className="group"><summary className="flex justify-between items-center cursor-pointer list-none"><span className="text-gray-800 font-medium">What is considered a "good" score?</span><span className="transition group-open:rotate-180"><svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg></span></summary><div className="mt-3 text-gray-600 text-sm"><p>Working memory capacity varies significantly between individuals. There isn't a single "good" score. The most valuable aspect of this assessment is tracking your own performance trend over time after the familiarization phase. Focus on your personal progress rather than comparing your score to others, especially initially.</p></div></details></div>
                        <div className="border-b border-gray-200 pb-4"><details className="group"><summary className="flex justify-between items-center cursor-pointer list-none"><span className="text-gray-800 font-medium">How can I improve my performance?</span><span className="transition group-open:rotate-180"><svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg></span></summary><div className="mt-3 text-gray-600 text-sm"><p>The key is to find a balance between accurately performing the processing task (counting and judging Odd/Even quickly but correctly) and effectively encoding and remembering the sequence of counts. Ensure you understand the instructions fully, minimize distractions during the task, and try to engage consistently with both parts of the task on every trial.</p></div></details></div>
                        <div className="pb-2"><details className="group"><summary className="flex justify-between items-center cursor-pointer list-none"><span className="text-gray-800 font-medium">Can I pause the task or switch tabs?</span><span className="transition group-open:rotate-180"><svg className="fill-current h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg></span></summary><div className="mt-3 text-gray-600 text-sm"><p>No. Once an assessment or familiarization session starts, it cannot be paused or stopped. Working memory tasks require sustained attention and focus. Pausing or switching away would disrupt the cognitive processes being measured and invalidate the results. Please ensure you have dedicated, uninterrupted time before starting a session.</p></div></details></div>
                    </div>
                 </div>
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirmStart}
                title={isConfirmingFamiliarization ? "Start Familiarization Session" : "Start Monthly Assessment"}
                confirmColor="indigo"
                confirmText={isConfirmingFamiliarization ? "Start Practice" : "Start Assessment"}
                cancelText="Cancel"
                // Require confirmation input ONLY for the official monthly assessment
                requiresConfirmationInput={!isConfirmingFamiliarization}
                expectedConfirmationValue={CONFIRMATION_WORD}
                confirmationPlaceholder={CONFIRMATION_WORD}
            >
                {/* Modal content remains the same */}
                {isConfirmingFamiliarization ? (
                    <div className="space-y-4">
                         <div className="flex justify-center"><div className="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center text-blue-600"><FontAwesomeIcon icon={faPlay} className="text-xl" /></div></div>
                        <p className="text-gray-700">You're about to start a <strong className="font-semibold">familiarization session</strong> for the Counting Span task. This practice session will help you understand how the task works without affecting your official scores.</p>
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-700"><FontAwesomeIcon icon={faInfoCircle} className="mr-2" /><span>This session cannot be paused once started and will take approximately 10-15 minutes to complete.</span></div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex justify-center"><div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center text-red-600"><FontAwesomeIcon icon={faExclamationTriangle} className="text-xl" /></div></div>
                        <p className="text-gray-700">You're about to start your <strong className="font-semibold">monthly assessment</strong> for the Counting Span task. This is an official test that will be recorded in your history and count as your assessment for this month.</p>
                        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700"><FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" /><span>This assessment <strong>cannot</strong> be paused or restarted once begun and will take approximately 15-20 minutes to complete.</span></div>
                    </div>
                )}
            </ConfirmationModal>
        </div> // End of main container div
    );
};

export default CountingSpanSummaryPage;